/*
  Akva — downloads på siden: forsider til kortene, og en visning af PDF, video
  og billede oven på siden, så man kan bladre en samlevejledning igennem uden
  at hente den først.

  Indlæses af snippets/akva-doc-viewer-setup.liquid, der også sætter
  window.akvaDocViewer (adresserne på PDF.js og teksterne). Kortene laves af
  snippets/akva-download-card.liquid.

  FORSIDER
  Et kort med data-cover-pdf har enten ingen forside endnu, eller en forside
  lavet af bin/download-covers med PDF'ens størrelse i data-cover-bytes. Når
  kortet kommer i syne:
    · forsiden findes, og størrelsen passer med filen → intet at gøre
    · ingen forside, eller filen er erstattet siden → side 1 tegnes her
  Størrelsen læses med et Range-kald på én byte. Et tjek der er gået godt,
  huskes pr. filadresse. Adressen skifter (?v=), når filen erstattes, så et
  gammelt "i orden" gælder aldrig for en ny fil.

  VISNINGEN
  <dialog> med showModal: fokus bliver i visningen, Esc lukker, og fokus går
  tilbage til kortet bagefter. PDF-siderne tegnes først, når de er tæt på at
  komme i syne, og lærreder langt fra skærmen gives slip igen. En
  samlevejledning på 27 sider i fuld opløsning ville ellers bruge flere hundrede
  MB, og iOS lukker fanen, når lærrederne tilsammen bliver for store.
*/

const config = window.akvaDocViewer || {};
const labels = Object.assign(
  { download: 'Download', close: 'Close', error: 'The preview could not be shown.' },
  config.labels
);
const absolute = (url) => new URL(url, location.href).href;
const pixelRatio = () => Math.min(window.devicePixelRatio || 1, 2);

// Temaets egen scroll-lås (assets/utilities.js). Den holder styr på hvem der
// låser, så visningen og fx menuskuffen ikke låser op for hinanden. Findes
// den ikke (en side uden for temaet), sættes attributten direkte.
const themeUtilities = import('@theme/utilities').catch(() => null);

async function lockPage(owner, locked) {
  const utilities = await themeUtilities;
  if (utilities?.lockScroll) {
    locked ? utilities.lockScroll(owner) : utilities.unlockScroll(owner);
  } else if (locked) {
    document.documentElement.setAttribute('scroll-lock', '');
  } else {
    document.documentElement.removeAttribute('scroll-lock');
  }
}

// Højst så mange pixels pr. lærred. iOS Safari tegner ikke lærreder over ca.
// 16 mio. pixels og har et loft over dem alle tilsammen.
const MAX_CANVAS_PIXELS = 8e6;

// ── PDF.js ──────────────────────────────────────────────────────────────────

let pdfjsReady = null;

function pdfjs() {
  if (!pdfjsReady) {
    pdfjsReady = import(absolute(config.pdfjs)).then((lib) => {
      lib.GlobalWorkerOptions.workerSrc = absolute(config.worker);
      return lib;
    });
    // Et netværksudfald må ikke låse PDF-visningen resten af besøget.
    pdfjsReady.catch(() => {
      pdfjsReady = null;
    });
  }
  return pdfjsReady;
}

function openPdf(lib, url, options = {}) {
  // isEvalSupported: false — PDF.js må ikke oversætte skrifttyper til kode.
  return lib.getDocument({ url: absolute(url), isEvalSupported: false, ...options });
}

// Lærredets bredde i pixels for en side, som den står på skærmen lige nu.
function canvasWidth(element, unscaled) {
  let width = element.clientWidth * pixelRatio();
  const height = width * (unscaled.height / unscaled.width);
  if (width * height > MAX_CANVAS_PIXELS) width *= Math.sqrt(MAX_CANVAS_PIXELS / (width * height));
  return Math.round(width);
}

function release(canvas) {
  if (!canvas) return;
  // Nulstil størrelsen, så iOS frigiver hukommelsen med det samme og ikke
  // først når lærredet bliver ryddet op.
  canvas.width = 0;
  canvas.height = 0;
  canvas.remove();
}

// ── Forsider ────────────────────────────────────────────────────────────────

const coverQueue = [];
let coversRunning = 0;

async function fileSize(url) {
  const controller = new AbortController();
  try {
    const response = await fetch(url, { headers: { Range: 'bytes=0-0' }, signal: controller.signal });
    const range = response.headers.get('content-range'); // "bytes 0-0/701015"
    const size = range ? Number(range.split('/').pop()) : Number(response.headers.get('content-length'));
    if (response.status === 206) {
      await response.arrayBuffer();
    } else {
      // Serveren sendte hele filen i stedet for én byte. Den skal ikke hentes.
      controller.abort();
    }
    return size > 0 ? size : null;
  } catch (error) {
    return null;
  }
}

async function coverIsCurrent(media) {
  if (!media.querySelector('img')) return false;
  const expected = media.dataset.coverBytes;
  // Et forsidebillede sat i hånden har ingen størrelse at tjekke. Det er et valg.
  if (!expected) return true;

  const key = 'akva-cover:' + absolute(media.dataset.coverPdf);
  try {
    if (localStorage.getItem(key) === expected) return true;
  } catch (error) {}

  const size = await fileSize(absolute(media.dataset.coverPdf));
  // Kan størrelsen ikke læses, er billedet det bedste bud.
  if (size === null) return true;
  if (String(size) !== expected) return false;
  try {
    localStorage.setItem(key, expected);
  } catch (error) {}
  return true;
}

async function drawCover(media) {
  if (await coverIsCurrent(media)) return;

  const lib = await pdfjs();
  // Kun de bytes, side 1 kræver — ikke hele filen.
  const task = openPdf(lib, media.dataset.coverPdf, { disableAutoFetch: true, disableStream: true });
  try {
    const page = await (await task.promise).getPage(1);
    const unscaled = page.getViewport({ scale: 1 });
    const box = media.getBoundingClientRect();
    const scale =
      Math.min((box.width * 0.8) / unscaled.width, (box.height * 0.86) / unscaled.height) * pixelRatio();
    const viewport = page.getViewport({ scale: Math.max(scale, 0.2) });
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(viewport.width);
    canvas.height = Math.round(viewport.height);
    canvas.className = 'akva-dl-card__cover';
    await page.render({ canvas, viewport }).promise;
    media.querySelectorAll('.akva-dl-card__cover, .akva-dl-card__format').forEach((old) => old.remove());
    media.prepend(canvas);
  } finally {
    task.destroy();
  }
}

function pumpCovers() {
  while (coversRunning < 2 && coverQueue.length) {
    const media = coverQueue.shift();
    coversRunning++;
    drawCover(media)
      // Forsiden er en hjælp, ikke en forudsætning. Fejler den, står kortet
      // med sit neutrale felt, og klik og download virker som før.
      .catch(() => {})
      .finally(() => {
        coversRunning--;
        pumpCovers();
      });
  }
}

const coverWatch =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            coverWatch.unobserve(entry.target);
            coverQueue.push(entry.target);
          });
          pumpCovers();
        },
        { rootMargin: '300px 0px' }
      )
    : null;

function watchCovers(root = document) {
  if (!coverWatch) return;
  root.querySelectorAll('[data-cover-pdf]:not([data-cover-watched])').forEach((media) => {
    media.setAttribute('data-cover-watched', '');
    coverWatch.observe(media);
  });
}

// ── Visningen ───────────────────────────────────────────────────────────────

const ZOOM_STEPS = [1, 1.5, 2, 3];

class Viewer {
  constructor() {
    const dialog = document.createElement('dialog');
    dialog.className = 'akva-viewer';
    dialog.setAttribute('aria-labelledby', 'akva-viewer-title');
    dialog.innerHTML = `
      <div class="akva-viewer__panel">
        <div class="akva-viewer__bar">
          <div class="akva-viewer__heading">
            <p class="akva-viewer__title" id="akva-viewer-title"></p>
            <p class="akva-viewer__detail"></p>
          </div>
          <div class="akva-viewer__actions">
            <div class="akva-viewer__langs" role="group"></div>
            <a class="akva-viewer__download" download target="_blank" rel="noopener">
              <span class="akva-viewer__download-label"></span>
              <span class="akva-viewer__format"></span>
            </a>
            <button type="button" class="akva-viewer__close">
              <svg aria-hidden="true" focusable="false" width="18" height="18" viewBox="0 0 18 18">
                <path d="M3.5 3.5l11 11M14.5 3.5l-11 11" stroke="currentColor" stroke-width="1.25" fill="none"/>
              </svg>
              <span class="visually-hidden"></span>
            </button>
          </div>
        </div>
        <div class="akva-viewer__body" tabindex="0"></div>
        <div class="akva-viewer__tools" hidden>
          <button type="button" class="akva-viewer__zoom" data-zoom="-1" aria-label="Zoom out">−</button>
          <span class="akva-viewer__pager" aria-live="polite"></span>
          <button type="button" class="akva-viewer__zoom" data-zoom="1" aria-label="Zoom in">+</button>
        </div>
      </div>`;
    document.body.append(dialog);

    this.dialog = dialog;
    this.title = dialog.querySelector('.akva-viewer__title');
    this.detail = dialog.querySelector('.akva-viewer__detail');
    this.langs = dialog.querySelector('.akva-viewer__langs');
    this.download = dialog.querySelector('.akva-viewer__download');
    this.body = dialog.querySelector('.akva-viewer__body');
    this.tools = dialog.querySelector('.akva-viewer__tools');
    this.pager = dialog.querySelector('.akva-viewer__pager');
    this.zoomOut = dialog.querySelector('[data-zoom="-1"]');
    this.zoomIn = dialog.querySelector('[data-zoom="1"]');
    this.token = 0;
    this.zoom = 1;
    this.pages = [];

    dialog.querySelector('.akva-viewer__download-label').textContent = labels.download;
    dialog.querySelector('.akva-viewer__close .visually-hidden').textContent = labels.close;
    dialog.querySelector('.akva-viewer__close').addEventListener('click', () => this.close());
    // Klik uden for panelet (på den mørke baggrund) lukker.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) this.close();
    });
    // Esc lukker dialogen uden om close(). Hændelsen kommer som en opgave bagefter
    // — i en skjult fane først meget senere — derfor rydder close() selv op.
    dialog.addEventListener('close', () => this.closed());
    this.zoomOut.addEventListener('click', () => this.setZoom(-1));
    this.zoomIn.addEventListener('click', () => this.setZoom(1));

    let ticking = false;
    this.body.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          this.updatePager();
        });
      },
      { passive: true }
    );

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (!dialog.open || !this.pages.length) return;
        this.fitWidth();
        this.pages.forEach((page) => page.firstChild && this.renderPage(page));
      }, 150);
    });

    // Sider tegnes, når de er inden for en skærmhøjde af det synlige, og gives
    // slip, når de er længere væk.
    this.pageWatch = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.renderPage(entry.target);
          else this.releasePage(entry.target);
        });
      },
      { root: this.body, rootMargin: '100% 0px' }
    );
  }

  open(data, trigger) {
    const files = (data.files || []).filter((file) => file.url);
    if (!files.length) return;

    this.trigger = trigger;
    this.title.textContent = data.title || '';
    this.detail.textContent = data.detail || '';
    this.detail.hidden = !data.detail;
    this.body.setAttribute('aria-label', [data.title, data.detail].filter(Boolean).join(', '));

    // Samme sprog som hent-linket på kortet: det foretrukne, ellers engelsk.
    const preferred = trigger.closest('[data-lang]')?.dataset.lang;
    const start =
      files.find((file) => file.lang && file.lang === preferred) || files.find((file) => file.lang === 'en') || files[0];

    const translated = files.filter((file) => file.lang);
    this.langs.replaceChildren(
      ...translated.map((file) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'akva-viewer__lang';
        button.textContent = file.lang.toUpperCase();
        button.addEventListener('click', () => this.show(file));
        button.file = file;
        return button;
      })
    );
    this.langs.hidden = translated.length < 2;

    this.active = true;
    lockPage(this.dialog, true);
    this.dialog.showModal();
    this.show(start);
    this.body.focus({ preventScroll: true });
  }

  show(file) {
    if (file === this.file && this.dialog.dataset.type) return;
    this.clear();
    const token = ++this.token;
    this.file = file;

    this.langs.querySelectorAll('button').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.file === file));
    });
    this.download.href = file.download || file.url;
    this.download.querySelector('.akva-viewer__format').textContent = file.format || '';
    this.dialog.dataset.type = file.type;
    this.tools.hidden = file.type !== 'pdf';

    if (file.type === 'pdf') this.showPdf(file, token);
    else if (file.type === 'video') this.showVideo(file);
    else if (file.type === 'image') this.showImage(file);
  }

  async showPdf(file, token) {
    this.zoom = 1;
    this.updateZoomButtons();
    this.body.classList.add('is-loading');
    const list = document.createElement('div');
    list.className = 'akva-viewer__pages';
    this.body.append(list);

    try {
      const lib = await pdfjs();
      if (token !== this.token) return;
      this.task = openPdf(lib, file.url);
      const doc = await this.task.promise;
      if (token !== this.token) return;
      this.doc = doc;

      // Alle sider får plads med det samme i side 1's format, så rullebjælken
      // passer fra start. Afviger en side, retter den sig, når den tegnes.
      const first = (await doc.getPage(1)).getViewport({ scale: 1 });
      if (token !== this.token) return;
      this.fitWidth();
      for (let number = 1; number <= doc.numPages; number++) {
        const page = document.createElement('div');
        page.className = 'akva-viewer__page';
        page.dataset.page = number;
        page.style.aspectRatio = `${first.width} / ${first.height}`;
        list.append(page);
        this.pages.push(page);
        this.pageWatch.observe(page);
      }
      this.updatePager();
    } catch (error) {
      if (token === this.token) this.fail();
    } finally {
      if (token === this.token) this.body.classList.remove('is-loading');
    }
  }

  showVideo(file) {
    const video = document.createElement('video');
    video.className = 'akva-viewer__video';
    video.controls = true;
    video.playsInline = true;
    video.preload = 'metadata';
    if (file.poster) video.poster = file.poster;
    video.src = file.url;
    video.addEventListener('error', () => this.fail(), { once: true });
    this.body.append(video);
    // Klikket på kortet tæller som brugerens handling, så browseren tillader afspilning.
    video.play().catch(() => {});
  }

  showImage(file) {
    const image = document.createElement('img');
    image.className = 'akva-viewer__image';
    image.alt = '';
    image.decoding = 'async';
    image.src = file.url;
    image.addEventListener('error', () => this.fail(), { once: true });
    this.body.append(image);
  }

  fail() {
    const message = document.createElement('p');
    message.className = 'akva-viewer__error';
    message.textContent = labels.error;
    this.clear();
    this.body.append(message);
  }

  // Sidebredden ved 100 %: hele panelets bredde, men ikke bredere end en side
  // man kan læse uden at dreje hovedet.
  fitWidth() {
    const list = this.body.querySelector('.akva-viewer__pages');
    const style = getComputedStyle(list || this.body);
    const inner = this.body.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    this.body.style.setProperty('--akva-page-fit', `${Math.max(Math.min(inner, 920), 200)}px`);
  }

  async renderPage(element) {
    const doc = this.doc;
    if (!doc || element.rendering) return;
    const token = this.token;
    element.rendering = true;
    let stale = false;
    try {
      const page = await doc.getPage(Number(element.dataset.page));
      if (token !== this.token || !element.isConnected) return;

      const unscaled = page.getViewport({ scale: 1 });
      element.style.aspectRatio = `${unscaled.width} / ${unscaled.height}`;
      const width = canvasWidth(element, unscaled);
      if (element.querySelector('canvas')?.width === width) return;

      const viewport = page.getViewport({ scale: width / unscaled.width });
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = Math.round(viewport.height);
      element.task = page.render({ canvas, viewport });
      await element.task.promise;
      if (token !== this.token || !element.isConnected) return release(canvas);

      release(element.querySelector('canvas'));
      element.append(canvas);
      // Blev der zoomet, mens siden blev tegnet, passer den ikke længere.
      stale = canvasWidth(element, unscaled) !== width;
    } catch (error) {
      // Aflyst, fordi siden rullede væk, blev zoomet eller visningen lukket.
    } finally {
      element.rendering = false;
      element.task = null;
    }
    if (stale) this.renderPage(element);
  }

  releasePage(element) {
    element.task?.cancel();
    release(element.querySelector('canvas'));
  }

  currentPage() {
    const line = this.body.scrollTop + this.body.clientHeight * 0.35;
    let current = this.pages[0];
    for (const page of this.pages) {
      if (page.offsetTop > line) break;
      current = page;
    }
    return current;
  }

  updatePager() {
    const page = this.pages.length ? this.currentPage() : null;
    this.pager.textContent = page ? `${page.dataset.page} / ${this.pages.length}` : '';
  }

  setZoom(direction) {
    const index = Math.max(0, Math.min(ZOOM_STEPS.length - 1, ZOOM_STEPS.indexOf(this.zoom) + direction));
    if (ZOOM_STEPS[index] === this.zoom || !this.pages.length) return;

    // Bliv på samme sted i dokumentet: samme side, samme andel nede på siden.
    const page = this.currentPage();
    const within = (this.body.scrollTop - page.offsetTop) / page.offsetHeight;
    this.zoom = ZOOM_STEPS[index];
    this.updateZoomButtons();
    this.body.scrollTop = page.offsetTop + within * page.offsetHeight;
    this.body.scrollLeft = (this.body.scrollWidth - this.body.clientWidth) / 2;
    this.pages.forEach((element) => element.querySelector('canvas') && this.renderPage(element));
  }

  updateZoomButtons() {
    this.body.style.setProperty('--akva-zoom', this.zoom);
    this.zoomOut.disabled = this.zoom === ZOOM_STEPS[0];
    this.zoomIn.disabled = this.zoom === ZOOM_STEPS[ZOOM_STEPS.length - 1];
  }

  clear() {
    this.token++;
    this.pageWatch.disconnect();
    this.pages.forEach((page) => this.releasePage(page));
    this.pages = [];
    this.task?.destroy();
    this.task = null;
    this.doc = null;
    this.body.querySelectorAll('video').forEach((video) => {
      video.pause();
      video.removeAttribute('src');
      video.load();
    });
    this.body.replaceChildren();
    this.body.classList.remove('is-loading');
    this.body.scrollTop = 0;
    this.body.scrollLeft = 0;
    delete this.dialog.dataset.type;
    this.pager.textContent = '';
  }

  close() {
    if (this.dialog.open) this.dialog.close();
    this.closed();
  }

  closed() {
    if (!this.active) return;
    this.active = false;
    this.clear();
    this.file = null;
    lockPage(this.dialog, false);
    this.trigger?.focus({ preventScroll: true });
  }
}

let viewer = null;

document.addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button !== 0) return;
  // cmd/ctrl/shift-klik betyder "åbn i ny fane" — det skal browseren have lov til.
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const link = event.target.closest?.('a[data-viewer]');
  if (!link || typeof HTMLDialogElement === 'undefined') return;

  let data;
  try {
    data = JSON.parse(link.dataset.viewer);
  } catch (error) {
    return;
  }
  if (!data.files?.some((file) => file.url)) return;

  event.preventDefault();
  viewer ||= new Viewer();
  viewer.open(data, link);
});

watchCovers();
// Temaeditoren genindlæser en sektion uden at genindlæse siden.
document.addEventListener('shopify:section:load', (event) => watchCovers(event.target));
